/**
 * Article CABYS Edit Page
 *
 * Dedicated page for editing the CABYS code of an article.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, Loader2, AlertTriangle, Search } from 'lucide-react'

import api from '@/lib/api'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useToast } from '@/components/ui/toaster'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useIsMobile } from '@/hooks/useIsMobile'

interface Article {
  id: number
  code: string
  description: string
  cabys_code: string | null
}

export function ArticleCabysPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // En el celular el autofoco abre el teclado y tapa la mitad de la pantalla
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Get return URL from query params
  const returnUrl = searchParams.get('return') || `/articles/${id}`

  // Fetch article data
  const { data: article, isLoading } = useQuery({
    queryKey: ['articles', Number(id)],
    queryFn: async () => {
      const response = await api.get(`/articles/${id}`)
      return response.data as Article
    },
    enabled: !!id,
  })

  // Form state
  const [cabysCode, setCabysCode] = useState('')

  // Initialize when article loads
  useEffect(() => {
    if (article) {
      setCabysCode(article.cabys_code || '')
    }
  }, [article])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.put(`/articles/${id}`, { cabys_code: code })
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['articles'], refetchType: 'all' })
      toast({ title: t('articles.cabysUpdatedSuccess'), variant: 'success' })
      navigate(returnUrl)
    },
    onError: () => {
      toast({ title: t('common.error'), variant: 'destructive' })
    },
  })

  const handleSubmit = () => {
    if (cabysCode.length !== 13) return
    updateMutation.mutate(cabysCode)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{t('articles.notFound')}</p>
        <Button variant="outline" onClick={() => navigate('/articles')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(returnUrl)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">
            {t('articles.updateCabys')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {article.code} - {article.description}
          </p>
        </div>
      </div>

      {/* Article Info */}
      <Card>
        <CardHeader>
          <CardTitle>{article.code}</CardTitle>
          <CardDescription>{article.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current CABYS */}
          {article.cabys_code ? (
            <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.cabysCurrent')}</p>
              <p className="font-mono text-lg">{article.cabys_code}</p>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('articles.cabysWarning')}
              </AlertDescription>
            </Alert>
          )}

          {/* CABYS Input */}
          <div className="space-y-2">
            <Label>{t('articles.cabysCode')} *</Label>
            <Input
              value={cabysCode}
              onChange={(e) => setCabysCode(e.target.value.replace(/\D/g, '').slice(0, 13))}
              placeholder={t('articles.cabysPlaceholder')}
              maxLength={13}
              className="h-14 text-xl font-mono text-center"
              autoFocus={!isMobile}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{cabysCode.length}/13 dígitos</span>
              <span>{t('articles.cabysRequired2025')}</span>
            </div>
          </div>

          {/* Validation indicator */}
          <div className={`p-3 rounded-lg ${
            cabysCode.length === 13
              ? 'bg-green-50 dark:bg-green-950/20 text-green-600'
              : cabysCode.length > 0
              ? 'bg-yellow-50 dark:bg-yellow-950/20 text-yellow-600'
              : 'bg-muted/50 text-muted-foreground'
          }`}>
            <p className="text-sm text-center">
              {cabysCode.length === 13
                ? t('articles.cabysValid')
                : cabysCode.length > 0
                ? t('articles.cabysMissingDigits', { count: 13 - cabysCode.length })
                : t('articles.cabysEnter13')}
            </p>
          </div>

          {/* Help link */}
          <div className="text-center">
            <a
              href="https://www.hacienda.go.cr/ATV/ComprobanteElectronico/cabys.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <Search className="h-3 w-3" />
              {t('articles.cabysSearchHacienda')}
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Footer Actions */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-background border-t z-50 md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent md:z-auto">
        <div className="flex gap-2 max-w-screen-lg mx-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(returnUrl)}
            className="flex-1 h-12"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={cabysCode.length !== 13 || updateMutation.isPending}
            className="flex-1 h-12"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
